import { inject, Injectable } from '@angular/core';
import { ChartThemeService } from '@sb/ui/chart';
import { Chart, ChartData, ChartDataset, ChartOptions, ChartType } from 'chart.js';

import { GraphSize } from '../enums';

export type ChartDataLine = ChartData<'line'>;

// Both the schedule and turnover reports arrive grouped by department name.
type ChartReportGroups<TRow> = Record<string, TRow[]>;

export interface ChartLineChart {
  data: ChartData<'line'>;
  type: ChartType;
  options: ChartOptions<'line'>;
}

const LABEL_MAX_LENGTH = 50;

@Injectable({
  providedIn: 'root',
})
export class ChartHelperService {
  private chartTheme = inject(ChartThemeService);

  lineChartOptions: ChartLineChart['options'] = {
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 20,
          usePointStyle: false,
          pointStyle: 'rectRounded',
          generateLabels: (chart) => {
            // Truncate text labels to maximum of LABEL_MAX_LENGTH characters
            const original = Chart.defaults.plugins.legend.labels.generateLabels;
            const labels = original(chart);
            labels.forEach((label) => {
              if (label.text.length > LABEL_MAX_LENGTH) {
                label.text = label.text.slice(0, LABEL_MAX_LENGTH - 3) + '...';
              }
            });
            return labels;
          },
        },
      },
      tooltip: {
        position: 'nearest',
        callbacks: {
          label: (context) => {
            // Truncate tooltip labels to maximum of LABEL_MAX_LENGTH characters
            let label = context.dataset.label || '';
            if (label.length > LABEL_MAX_LENGTH) {
              label = label.slice(0, LABEL_MAX_LENGTH - 3) + '...';
            }
            if (context.parsed.y !== null) {
              label += `: ${context.parsed.y}`;
            }
            return label;
          },
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  private heightMap: Record<GraphSize, string> = {
    small: '288px',
    medium: '400px',
  };

  getHeight(size: GraphSize): string {
    return this.heightMap[size];
  }

  convertToLineChart<TRow>(
    labels: string[],
    groups: ChartReportGroups<TRow>,
    selectValue: (row: TRow) => number,
  ): ChartLineChart {
    const data = this.mapToLineChartData(labels, groups, selectValue);
    return {
      data,
      type: 'line',
      options: this.lineChartOptions,
    };
  }

  private mapToLineChartData<TRow>(
    labels: string[],
    groups: ChartReportGroups<TRow>,
    selectValue: (row: TRow) => number,
  ): ChartDataLine {
    const keys = Object.keys(groups);
    const colors = this.chartTheme.getColorsByDataSize(keys.length);

    const data: ChartDataset<'line'>[] = keys.map((key, index) => ({
      label: key.replace(/\([0-9]*?\)/g, ''),
      data: groups[key].map((row) => selectValue(row)),
      borderColor: colors[index],
      backgroundColor: colors[index],
      tension: 0.2,
      cubicInterpolationMode: 'monotone',
      borderWidth: 3,
      pointBorderColor: colors[index],
      pointBackgroundColor: colors[index],
      pointHoverBackgroundColor: '#ffffff',
      pointHoverBorderColor: colors[index],
      pointHoverRadius: 8,
      pointHoverBorderWidth: 3,
      pointHitDetectionRadius: 35,
    }));

    return {
      labels,
      datasets: data,
    };
  }
}
